// Utility functions and hooks for multi-tenancy admin permissions

/**
 * Get the user's role within their tenant
 * @param {Object} tenantContext - The tenant context from AppContext
 * @param {Object} user - The user object from AppContext
 * @returns {string} The user's role: 'league_admin', 'moderator', or 'player'
 */
export function getTenantRole(tenantContext, user) {
  return tenantContext?.membership?.role || 
         (user?.role === 'admin' ? 'league_admin' : 'player');
}

/**
 * Check if user is a League Admin
 * @param {Object} tenantContext - The tenant context from AppContext
 * @param {Object} user - The user object from AppContext
 * @returns {boolean}
 */
export function isLeagueAdmin(tenantContext, user) {
  const role = getTenantRole(tenantContext, user);
  return role === 'league_admin' || user?.role === 'admin';
}

/**
 * Check if user is at least a Moderator
 * @param {Object} tenantContext - The tenant context from AppContext
 * @param {Object} user - The user object from AppContext
 * @returns {boolean}
 */
export function isModeratorOrAbove(tenantContext, user) {
  const role = getTenantRole(tenantContext, user);
  return role === 'league_admin' || role === 'moderator' || user?.role === 'admin';
}

/**
 * Check if user can access admin pages
 * @param {Object} tenantContext - The tenant context from AppContext
 * @param {Object} user - The user object from AppContext
 * @returns {boolean}
 */
export function canAccessAdmin(tenantContext, user) {
  return isModeratorOrAbove(tenantContext, user);
}

/**
 * Permission matrix for different admin actions
 * 
 * League Admin can:
 * - Everything
 * 
 * Moderator can:
 * - Manage contestants
 * - Manage scoring events/event log
 * - Manage bonus questions (but not grading windows in settings)
 * - View players (but not invite, delete, or change roles)
 * - View audit log
 * 
 * Moderator CANNOT:
 * - Create/delete seasons
 * - Edit league settings
 * - Invite/delete users
 * - Change user roles
 * - Manage notifications
 * - Import data
 * - Manage draft settings
 */

export const PERMISSIONS = {
  // Season management
  CREATE_SEASON: ['league_admin'],
  DELETE_SEASON: ['league_admin'],
  EDIT_SEASON: ['league_admin'],
  
  // Player management
  INVITE_PLAYERS: ['league_admin'],
  DELETE_PLAYERS: ['league_admin'],
  CHANGE_PLAYER_ROLES: ['league_admin'],
  VIEW_PLAYERS: ['league_admin', 'moderator'],
  
  // Contestant management
  MANAGE_CONTESTANTS: ['league_admin', 'moderator'],
  
  // Scoring
  MANAGE_SCORING_RULES: ['league_admin'],
  MANAGE_EVENT_LOG: ['league_admin', 'moderator'],
  RECALCULATE_SCORES: ['league_admin', 'moderator'],
  
  // Bonus questions
  MANAGE_BONUS_QUESTIONS: ['league_admin', 'moderator'],
  GRADE_BONUS_QUESTIONS: ['league_admin', 'moderator'],
  
  // Draft
  MANAGE_DRAFT: ['league_admin'],
  
  // Notifications
  MANAGE_NOTIFICATIONS: ['league_admin'],
  
  // Import
  IMPORT_DATA: ['league_admin'],
  
  // League settings
  EDIT_LEAGUE_SETTINGS: ['league_admin'],
  
  // Episode calendar
  MANAGE_EPISODE_CALENDAR: ['league_admin', 'moderator'],
  
  // Notes
  MANAGE_NOTES: ['league_admin', 'moderator'],
  
  // Audit log
  VIEW_AUDIT_LOG: ['league_admin', 'moderator'],
};

/**
 * Check if user has a specific permission
 * @param {string} permission - The permission to check
 * @param {Object} tenantContext - The tenant context from AppContext
 * @param {Object} user - The user object from AppContext
 * @returns {boolean}
 */
export function hasPermission(permission, tenantContext, user) {
  const role = getTenantRole(tenantContext, user);
  const allowedRoles = PERMISSIONS[permission];
  
  if (!allowedRoles) {
    console.warn(`Unknown permission: ${permission}`);
    return false;
  }
  
  // Legacy support: user.role === 'admin' is treated as league_admin
  if (user?.role === 'admin') {
    return allowedRoles.includes('league_admin');
  }
  
  return allowedRoles.includes(role);
}
